import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppUser, UserGroupEnum } from '../authorization/models';
import { KeyTypeEnum, type SaveResult } from '../../shared/models/save-result';
import type { SurveyClientAdminForList } from '../../features/admin/shared/models';

/**
 * API service for user-related operations.
 * Handles user profile management, validation, and retrieval.
 */
@Injectable({ providedIn: 'root' })
export class UserApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = 'api/user';

  /**
   * Validates if an email is available for use.
   * @param email - The email address to validate
   * @returns Observable of the user ID if email exists, 0 otherwise
   */
  validateEmail(email: string): Observable<number> {
    return this.http.get<number>(`${this.baseUrl}/validateemail/${email}`);
  }

  /**
   * Validates an email and retrieves the associated user if found.
   * @param email - The email address to validate
   * @returns Observable of the AppUser if found
   */
  validateAndGetEmail(email: string): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.baseUrl}/validateget/${email}`);
  }

  /**
   * Retrieves a user by their ID.
   * @param userId - The user's ID (use 0 for current user)
   * @param includeGroups - Whether to include user group information
   * @returns Observable of the AppUser
   */
  getUser(userId: number, includeGroups = false): Observable<AppUser> {
    return this.http.get<AppUser>(`${this.baseUrl}/${userId}/${includeGroups}`);
  }

  /**
   * Retrieves all admin users.
   * @returns Observable of admin users array
   */
  getAdmins(): Observable<AppUser[]> {
    return this.http.get<AppUser[]>(`${this.baseUrl}/admins`);
  }

  /**
   * Retrieves users by their group membership.
   * @param userGroup - The user group to filter by
   * @returns Observable of users in the specified group
   */
  getUsersByGroup(userGroup: UserGroupEnum): Observable<AppUser[]> {
    return this.http.get<AppUser[]>(`${this.baseUrl}/groups/${+userGroup}`);
  }

  /**
   * Retrieves users associated with a specific client.
   * @param surveyClientId - The client ID to filter by
   * @param excludeSurveyId - Survey ID to exclude from results
   * @returns Observable of users for the client
   */
  getUsersByClient(surveyClientId: number, excludeSurveyId: number): Observable<AppUser[]> {
    return this.http.get<AppUser[]>(`${this.baseUrl}/client/${surveyClientId}/${excludeSurveyId}`);
  }

  /**
   * Saves user profile updates.
   * @param user - The updated user data
   * @returns Observable of the save operation result
   */
  save(user: AppUser): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}`, user);
  }

  /**
   * Retrieves all client administrators.
   * @returns Observable of client admin list
   */
  getClientAdmins(): Observable<SurveyClientAdminForList[]> {
    return this.http.get<SurveyClientAdminForList[]>(`${this.baseUrl}/clientadmins`);
  }

  /**
   * Saves an admin user (create or update).
   * @param user - The admin user to save
   * @returns Observable of save result with new keys
   */
  saveAdmin(user: AppUser): Observable<SaveResult<KeyTypeEnum, number>> {
    return this.http.post<SaveResult<KeyTypeEnum, number>>(`${this.baseUrl}/admin`, user);
  }
}
